import { Link } from "react-router-dom";
import {
  ArrowLeft, MessageCircle, Mail, Clock, FileSearch, Shield,
  CreditCard, UserPlus, ScrollText, ShieldAlert, KeyRound,
} from "lucide-react";
import PublicFooter from "../components/PublicFooter";
import LastUpdatedBadge from "../components/LastUpdatedBadge";
import usePageMeta from "../hooks/usePageMeta";

const CHANNELS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "+254 XXX XXX XXX",
    href: null, // placeholder — link disabled until real number is set
    note: "Fastest channel for claim help and FlashClaim follow-ups.",
    testid: "support-whatsapp",
  },
  {
    icon: Mail,
    label: "Email",
    value: "support@nyoksure.com",
    href: "mailto:support@nyoksure.com",
    note: "Best for documents, screenshots, and detailed questions.",
    testid: "support-email",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Monday – Saturday · 8:00 am – 6:00 pm EAT",
    href: null,
    note: "Outside of these hours, we'll respond on the next working day.",
    testid: "support-hours",
  },
];

const TOPICS = [
  { icon: FileSearch, label: "Claims" },
  { icon: CreditCard, label: "Payments" },
  { icon: UserPlus, label: "Merchant onboarding" },
  { icon: ScrollText, label: "Policy status" },
  { icon: ShieldAlert, label: "Fraud review" },
  { icon: KeyRound, label: "Account access" },
];

export default function Support() {
  usePageMeta(
    "Support · Nyoksure",
    "Reach Nyoksure support via WhatsApp, email or support ticket — for claims, payments, onboarding, fraud review, and account access.",
  );
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#114232]" data-testid="support-page">
      <header className="px-6 lg:px-10 py-6 max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/" className="text-xs text-[#5c6360] hover:text-[#114232] inline-flex items-center gap-1"
              data-testid="back-to-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Nyoksure
        </Link>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#c15337]">Support</div>
      </header>
      <main className="px-6 lg:px-10 max-w-3xl mx-auto py-4">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Talk to us</h1>
        <div className="mb-4"><LastUpdatedBadge pageKey="support" /></div>
        <p className="text-sm sm:text-base text-[#5c6360] mb-8 max-w-2xl">
          Our team helps merchants across Kenya with cover, claims, and
          everything in between. Pick the channel that works for you.
        </p>

        <section data-testid="support-channels" className="space-y-3 mb-8">
          {CHANNELS.map((c) => {
            const Wrapper = c.href ? "a" : "div";
            const props = c.href ? { href: c.href } : {};
            return (
              <Wrapper
                key={c.label}
                {...props}
                data-testid={c.testid}
                className={`block bg-white border border-[#114232]/10 rounded-xl p-5 sm:p-6 ${c.href ? "hover:border-[#c15337]/30 hover:shadow-sm transition-all" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <c.icon className="w-5 h-5 text-[#c15337] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360]">{c.label}</div>
                    <div className="font-heading text-lg sm:text-xl font-bold mt-0.5">{c.value}</div>
                    <div className="text-sm text-[#5c6360] mt-1">{c.note}</div>
                  </div>
                </div>
              </Wrapper>
            );
          })}
        </section>

        <section className="bg-white border border-[#114232]/10 rounded-xl p-5 sm:p-6 mb-8"
                  data-testid="support-topics">
          <h2 className="font-heading text-lg sm:text-xl font-bold mb-3">What can we help with?</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {TOPICS.map((t) => (
              <div key={t.label}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[#f4f2eb] border border-[#114232]/10 min-h-11"
                    data-testid={`support-topic-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <t.icon className="w-4 h-4 text-[#c15337] shrink-0" />
                <span className="text-sm font-medium text-[#114232]">{t.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-[#114232]/5 border border-[#114232]/15 rounded-xl p-5 sm:p-6 mb-8"
                  data-testid="support-ticket-cta">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-[#c15337] mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-lg sm:text-xl font-bold mb-1">Already a merchant?</h2>
              <p className="text-sm sm:text-base text-[#1b1d1c]">
                Open a support ticket directly from your dashboard for the
                fastest response — we'll match it to your account and policy
                automatically.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center mt-3 text-sm font-semibold text-[#c15337] hover:underline"
                data-testid="support-go-to-dashboard"
              >
                Go to merchant dashboard →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
